/**
 * スタンプラリーとルートプランの地図シェル
 *
 * /rallies と /route-plans は URL を消費しないレイアウトルートで束ねてある。
 * 画面を移ってもこの要素は作り直されないので、地図インスタンスとその中心・ズームが残る。
 * 画面ごとの中身は use*Screen フックが組み立て、ここは器だけを持つ。
 */

import { useRef } from 'react'
import { useMatch } from 'react-router'

import { Header } from '@/components/shared/header'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { GoogleMapCanvas } from '@/components/shared/map/google-map-canvas'
import { RoutePathModeToggle } from '@/components/shared/map/route-path-mode-toggle'
import { useFitBounds } from '@/components/shared/map/use-fit-bounds'
import { BottomSheet, type BottomSheetRef } from '@/components/ui/bottom-sheet'
import { useCurrentPosition } from '@/hooks/useCurrentPosition'
import { useGoBack } from '@/hooks/useGoBack'
import { useRallyScreen } from '@/pages/rallies/use-rally-screen'
import { usePlanDetailScreen } from '@/pages/route-plans/detail/use-plan-detail-screen'
import { usePlanListScreen } from '@/pages/route-plans/use-plan-list-screen'
import { PATHS } from '@/routes/paths'

import { SHEET_SNAP, SHEET_SNAP_POINTS } from './types'

const ORIGIN_COLOR = '#3B82F6'

/** useFitBounds はマップのコンテキスト内でしか使えないので子として置く */
function MapFitter({ points, fitKey }: { points: google.maps.LatLngLiteral[]; fitKey: string }) {
  useFitBounds({ points, padding: 48, maxZoom: 14, fitKey })
  return null
}

const toId = (value: string | undefined): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const MapShell = () => {
  const goBack = useGoBack()
  const sheetRef = useRef<BottomSheetRef>(null)
  const { coords, request: requestPosition } = useCurrentPosition()

  const rallyList = useMatch(PATHS.RALLIES.LIST)
  const rallyDetail = useMatch(PATHS.RALLIES.DETAIL)
  const planDetail = useMatch(PATHS.ROUTE_PLANS.DETAIL)

  const isRally = rallyList !== null || rallyDetail !== null
  const planId = toId(planDetail?.params.plan_id)
  const isPlanDetail = !isRally && planId !== null

  const rallyScreen = useRallyScreen({
    active: isRally,
    rallyId: toId(rallyDetail?.params.rally_id),
    origin: coords,
    requestPosition,
    sheetRef,
    snap: SHEET_SNAP,
  })

  const planListScreen = usePlanListScreen({
    active: !isRally && !isPlanDetail,
    sheetRef,
    snap: SHEET_SNAP,
  })

  const planDetailScreen = usePlanDetailScreen({
    active: isPlanDetail,
    // 一覧を見ているあいだも呼ぶが active=false なので取得は走らない
    planId: planId ?? 0,
    coords,
    requestPosition,
    sheetRef,
    snap: SHEET_SNAP,
  })

  const screen = isRally ? rallyScreen : isPlanDetail ? planDetailScreen : planListScreen

  return (
    <div className="flex h-dvh flex-col">
      {/* 常設シートは Header より手前に出るので、メニューをさらに上へ持ち上げる */}
      <Header
        title={screen.title}
        onClose={goBack}
        dropdownSections={screen.menuSections}
        dropdownContentClassName="z-[10000000]"
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {screen.banner}

        {(!isRally || screen.filterBar) && (
          <div className="flex shrink-0 items-center border-b bg-background">
            {/* 経路を持つのはルートプランだけなので、ラリーでは出さない */}
            {!isRally && (
              <div className="shrink-0 py-2 pl-3">
                <RoutePathModeToggle />
              </div>
            )}
            {screen.filterBar && <div className="min-w-0 flex-1">{screen.filterBar}</div>}
          </div>
        )}

        <div className="relative min-h-0 flex-1">
          <GoogleMapCanvas className="h-full w-full" mapTypeControl onClick={screen.onMapClick}>
            <MapFitter points={screen.fitPoints} fitKey={screen.fitKey} />

            {coords && (
              <GoogleCircleMarker
                position={{ lat: coords.latitude, lng: coords.longitude }}
                radius={6}
                color="#FFFFFF"
                fillColor={ORIGIN_COLOR}
                weight={2}
                zIndex={5}
              />
            )}

            {screen.mapLayer}
          </GoogleMapCanvas>

          {screen.mapOverlay}
        </div>
      </div>

      <BottomSheet
        ref={sheetRef}
        persistent
        open
        onOpenChange={() => undefined}
        noOverlay
        disableDismiss
        title={screen.title}
        headerContent={screen.sheetHeader}
        snapPoints={SHEET_SNAP_POINTS}
        defaultSnapPoint={SHEET_SNAP.half}
        contentClassName="space-y-0"
      >
        {screen.sheetBody}
      </BottomSheet>

      {screen.dialogs}
    </div>
  )
}
